// Data shapes shared by the workspace, renderers, and the same-origin API.
// Keep these close to the UI contract until the Rust API gains generated schemas.
import { isDimensionUnitsValue } from "./dimension-units";
import type { DimensionUnits } from "./dimension-units";

export type SampleKey = "1600i" | "plx" | "k1220" | "p2";

export const SHEET_SIZES = {
  ansi: [
    ["ansi-a", "ANSI A · 11 × 8½ in"],
    ["ansi-b", "ANSI B · 17 × 11 in"],
    ["ansi-c", "ANSI C · 22 × 17 in"],
    ["ansi-d", "ANSI D · 34 × 22 in"],
    ["ansi-e", "ANSI E · 44 × 34 in"],
  ],
  iso: [
    ["iso-a4", "ISO A4 · 297 × 210 mm"],
    ["iso-a3", "ISO A3 · 420 × 297 mm"],
    ["iso-a2", "ISO A2 · 594 × 420 mm"],
    ["iso-a1", "ISO A1 · 841 × 594 mm"],
    ["iso-a0", "ISO A0 · 1189 × 841 mm"],
  ],
} as const;
export type SheetSizeCode = (typeof SHEET_SIZES)["ansi" | "iso"][number][0];
export type SheetStyleCode = "classic" | "compact";
export const defaultSheetSize = (standard: "ansi" | "iso"): SheetSizeCode =>
  standard === "ansi" ? "ansi-d" : "iso-a3";
export const sheetSizeLabel = (size: SheetSizeCode) => size.toUpperCase().replace("-", " ");
export const isSheetSize = (value: unknown, standard: "ansi" | "iso"): value is SheetSizeCode =>
  SHEET_SIZES[standard].some(([code]) => code === value);
export const isSheetStyle = (value: unknown): value is SheetStyleCode =>
  value === "classic" || value === "compact";

export type Job = {
  api_created?: boolean;
  id: string;
  source_upload_id?: string;
  source?: "upload" | "sample";
  sample?: SampleKey;
  filename: string;
  drawing_name?: string;
  uploaded_at_ms?: number;
  saved_at_ms?: number;
  file_size_bytes?: number;
  standard: "ansi" | "iso";
  /** A {@link Quality}, or the name of a profile v0.2.0 retired. */
  quality: string;
  /** Set when the drawing was made with a retired profile: its old name. */
  retired_engine?: string;
  projection: "first" | "third";
  status: "queued" | "running" | "completed" | "failed";
  progress: number;
  error: string | null;
  timings: { name: string; elapsed_ms: number }[] | null;
  total_ms: number | null;
  model3d?: {
    status: "queued" | "generating" | "ready" | "failed";
    url: string | null;
    error: string | null;
  };
  /** A running conversion that streams its views: the provisional sheet. */
  views?: JobViews;
  /**
   * When the STEP file it was made from was removed, by expiry or by deleting
   * the upload. The drawing stays but can't be recalculated or get new views.
   */
  source_removed_at_ms?: number | null;
};

export type JobViewId = "front" | "top" | "right" | "isometric";

/**
 * The provisional sheet of a streaming conversion: page size in points and,
 * per primary view, its rectangle (y up, as in the drawing spec), progress,
 * and whether its lines can be fetched. The finished drawing replaces it and
 * may lay the views out slightly differently.
 */
export type JobViews = {
  page: { width_pt: number; height_pt: number };
  items: {
    id: JobViewId;
    /** Physical calibration of this view's provisional sheet coordinates. */
    points_per_mm?: number;
    progress: number;
    done: boolean;
    /** Skipped by the user while it was being solved: it will not be drawn. */
    skipped: boolean;
    rect: { x: number; y: number; width: number; height: number };
    lines_url: string | null;
  }[];
};

/** A streamed view's visible lines: `flat-v1`, every style `v`. */
export type ViewLines = { encoding: "flat-v1"; coordinates: number[]; styles: string };

/** One distinct part of a STEP model, as the Parts page lists it. */
export type PartSummary = {
  index: number;
  name: string;
  /** How many times the assembly places it. */
  quantity: number;
  /** Extent along the part's own x, y and z, in millimetres. */
  size_mm: [number, number, number] | null;
  faces: number;
  edges: number;
  /** Encloses a volume; an open part is a set of sheets. */
  closed: boolean;
  thumbnail: boolean;
  millis: number;
  error?: string;
  /** Measured and drawn; before that only the name and quantity are known. */
  computed: boolean;
};

export type PartsReport = {
  version: number;
  status: "running" | "completed" | "failed";
  /** Unknown until the model has been read. */
  total: number | null;
  done: number;
  parts: PartSummary[];
  error?: string;
};

export type MeasuredFeature =
  | {
      kind: "hole";
      body: number;
      body_bounds?: { min: ViewerPoint; max: ViewerPoint };
      start: ViewerPoint;
      end: ViewerPoint;
      axis: ViewerPoint;
      diameter: number;
      depth: number;
      through: boolean;
      /**
       * The opening at `start`, and at `end`, can be seen from outside along
       * the axis: square to a world axis, on its body's box, with no other
       * body over it.
       */
      exposed?: [boolean, boolean];
    }
  | {
      kind: "slot";
      body: number;
      body_bounds?: { min: ViewerPoint; max: ViewerPoint };
      start: ViewerPoint;
      end: ViewerPoint;
      normal: ViewerPoint;
      width: number;
      length: number;
      /** The slot can be seen from outside along `normal`, as a hole's `exposed`. */
      exposed?: boolean;
    }
  | { kind: "edge"; body: number; start: ViewerPoint; end: ViewerPoint };

export type MeasuredGeometry = {
  format_version: number;
  bounding_box: { min: ViewerPoint; max: ViewerPoint };
  geometry: { measured?: MeasuredFeature[] };
};

/** One face, edge or vertex a PMI item belongs to, in model millimetres. */
export type PmiReference = {
  shape: "face" | "edge" | "vertex" | "other";
  surface: "plane" | "cylinder" | "cone" | "sphere" | "torus" | "line" | "circle" | "other";
  origin: ViewerPoint;
  axis: ViewerPoint;
  radius: number;
  start: ViewerPoint;
  end: ViewerPoint;
  point: ViewerPoint;
  min: ViewerPoint;
  max: ViewerPoint;
};
/** Signed deviations from the nominal. */
export type PmiTolerance = { upper: number; lower: number };
type PmiValue = {
  value: number;
  tolerance?: PmiTolerance;
  limits?: [number, number];
  basic?: boolean;
  reference?: boolean;
};
/** Semantic PMI from the STEP file; `unresolved` says why an item cannot be drawn as read. */
export type SourcePmiItem = (
  | ({
      kind: "size";
      measure:
        | "diameter"
        | "radius"
        | "spherical_diameter"
        | "spherical_radius"
        | "thickness"
        | "angle"
        | "other";
      count: number;
    } & PmiValue)
  | ({ kind: "location"; measure: "linear" | "angle" } & PmiValue)
  | {
      kind: "tolerance";
      characteristic: GdtCharacteristic | "";
      value: number;
      zone?: "diameter" | "spherical";
      modifiers?: ToleranceModifier[];
      projected?: number;
      all_around?: boolean;
      datums?: DatumReference[];
    }
  | { kind: "datum"; label: string }
) & { geometry: PmiReference[]; second?: PmiReference[]; unresolved?: string };
export type SourcePmi = { items: SourcePmiItem[]; presentation_only: number };
export type SourcePmiGeometry = {
  format_version: number;
  bounding_box: { min: ViewerPoint; max: ViewerPoint };
  geometry: { source_pmi?: SourcePmi };
};

export type PartAnalysis = {
  format_version: number;
  model_units: string;
  bounding_box: { min: [number, number, number]; max: [number, number, number] };
  /** Dimensions are in the imported model's X/Y/Z axis order. */
  dimensions: [number, number, number];
  volume: number;
  surface_area: number;
  estimated_mass_kg: number | null;
  estimated_mass_material: string | null;
  body_count: number;
  component_count: number | null;
  geometry: {
    /** B-rep coordinates and sizes in millimetres, not the declared source unit. */
    measured?: MeasuredFeature[];
    hole_count: number | null;
    hole_diameters: number[] | null;
    through_holes: number | null;
    blind_holes: number | null;
    pockets: number | null;
    slots: number | null;
    fillets: number | null;
    chamfers: number | null;
    threads: number | null;
    minimum_wall_thickness: number | null;
    deep_cavities: number | null;
    internal_corners: number | null;
    recognition_confidence: string;
    recognition_notes: string[];
  };
  manufacturing: {
    likely_process: string;
    confidence: string;
    possible_setups: string[];
    stock_size: [number, number, number];
    material_utilization_percent: number | null;
    difficult_to_access_features: string | null;
    notes: string[];
  };
};

export type Upload = {
  api_created?: boolean;
  id: string;
  filename: string;
  source?: "upload" | "sample";
  sample?: SampleKey;
  uploaded_at_ms?: number;
  file_size_bytes?: number;
  /** Its expiry counts from here (`step_retention_days`). */
  last_used_at_ms?: number;
};

export type TitleBlock = {
  drawing_number: string;
  part_name: string;
  revision: string;
  drawn_by: string;
  date: string;
  scale: string;
  tolerances: string;
  company_name: string;
  sheet: string;
  checked_by: string;
  approved_by: string;
  sheet_size: string;
  material_finish: string;
  weight_mass: string;
  cage_code: string;
  legal_notice: string;
  logo_svg: string | null;
};

export type TitleField = keyof Omit<TitleBlock, "logo_svg">;
export type AxisFlips = { x: boolean; y: boolean; z: boolean };
export type ViewerRotation = { x: number; y: number; z: number };
export type ViewerProjection = "orthographic" | "perspective";
/**
 * How deep into the model a 2D view draws.
 *
 * `normal` is the outline as the eye would see it. `level-2` adds what lies one
 * body behind, which is the hidden detail a drawing usually wants. `xray` adds
 * everything else, which on a dense assembly is most of the linework.
 */
export type DepthMode = "normal" | "level-2" | "xray";

/**
 * The 3D viewer's own depth control, which is a different question: whether to
 * depth-test, to cull back faces so only the nearest surface draws, or to draw
 * everything. Kept separate from the drawing's ladder because "first surface"
 * means something concrete here and nothing in a 2D projection.
 */
export type ViewerDepthMode = "normal" | "first-hit" | "xray";
export type ViewerSelection = { min: [number, number, number]; max: [number, number, number] };
export type ViewerPoint = [number, number, number];
export type ViewerSource2D = {
  id: string;
  view_id: string;
  start: Point;
  end: Point;
  label_point?: Point;
};
/** Measure tool kinds, then the annotate kinds that share the markup list. */
export type MeasurementMode =
  "point-to-point" | "segment" | "circle" | "radius" | "angle" | "feature" | AnnotationKind;
export type MeasurementFeature = "diameter" | "radius" | "angle" | "length" | "width" | "height";
export const ANNOTATION_KINDS = ["note", "frame", "datum", "target", "surface"] as const;
export type AnnotationKind = (typeof ANNOTATION_KINDS)[number];
export const isAnnotationKind = (mode?: string): mode is AnnotationKind =>
  (ANNOTATION_KINDS as readonly string[]).includes(mode ?? "");
/** GD&T characteristics; which are offered depends on the sheet standard. */
export type GdtCharacteristic =
  | "straightness"
  | "flatness"
  | "circularity"
  | "cylindricity"
  | "line-profile"
  | "surface-profile"
  | "angularity"
  | "perpendicularity"
  | "parallelism"
  | "position"
  | "concentricity"
  | "symmetry"
  | "runout"
  | "total-runout";
/** Material condition and zone modifiers: MMC, LMC, free state, projected zone, tangent plane, unequally disposed. */
export type ToleranceModifier = "M" | "L" | "F" | "P" | "T" | "U";
export type DatumReference = { label: string; modifiers?: ("M" | "L")[] };
export type FrameRow = {
  characteristic: GdtCharacteristic;
  zone?: "diameter" | "spherical";
  tolerance: number;
  modifiers?: ToleranceModifier[];
  datums?: DatumReference[];
};
/** One row, or two: a composite frame when both rows share a characteristic, else stacked frames. */
export type FeatureControlFrame = { rows: FrameRow[] };
export type DatumTarget = { label: string; shape: "point" | "line" | "area"; size?: number };
export type SurfaceFinish = {
  process: "any" | "removal" | "no-removal";
  roughness?: string;
  method?: string;
  lay?: string;
};
/** What an annotation says; which field applies follows its `mode`. */
export type AnnotationContent = {
  frame?: FeatureControlFrame;
  datum?: string;
  target?: DatumTarget;
  surface?: SurfaceFinish;
  /** The all-around circle at the leader's elbow. */
  all_around?: boolean;
};
export type DimensionTolerance = { display: "limits" | "deviation"; upper: number; lower: number };
/** How a measured dimension is presented; tolerances are in the displayed unit. */
export type DimensionFormat = {
  style?: "basic" | "reference";
  /** Pattern count, drawn as "8X". */
  count?: number;
  tolerance?: DimensionTolerance;
  /** A frame and datum label attached under the dimension's text. */
  frame?: FeatureControlFrame;
  datum?: string;
};
export type ViewerMeasurement = {
  id: string;
  start: ViewerPoint;
  end: ViewerPoint;
  mode?: MeasurementMode;
  feature?: MeasurementFeature;
  /** Annotations: `start` is the surface point marked, `label_point` where the content sits. */
  note?: string;
  annotation?: AnnotationContent;
  /** Surface normal at an annotation's anchor, used to pick its 2D view. */
  normal?: ViewerPoint;
  /** Model-space point used as the label anchor; absent means automatic placement. */
  label_point?: ViewerPoint;
  /** A derived entry mirrored from a saved 2D custom dimension. */
  source_2d?: ViewerSource2D;
  /** Derived leaders for a counted 2D callout, in model space. */
  additional_leaders?: ViewerPoint[];
  /** A mirrored label dropped in 3D: drawn where it was put, not moved onto its view's plane. */
  label_fixed?: boolean;
  /**
   * A measurement made in 3D whose dimension line was moved off the measured
   * points, as a model-space vector; extension lines join it to them. The
   * sheet keeps its own lane for the same measurement.
   */
  offset?: ViewerPoint;
};
export type ViewerSettings = {
  crop_box: ViewerSelection | null;
  rotation: ViewerRotation;
  projection: ViewerProjection;
  depth_mode: ViewerDepthMode;
  measurements: ViewerMeasurement[];
};
export type CameraPreset = "front" | "back" | "left" | "right" | "top" | "bottom" | "isometric";
export type Theme = "light" | "dark";

export type DrawingSettings = {
  standard: "ansi" | "iso";
  sheet_size: SheetSizeCode;
  sheet_style?: SheetStyleCode;
  sheet_auto?: boolean;
  sheet_orientation?: "landscape" | "portrait";
  /** Resolved paper/model ratio, pinned when accepting an added-view layout. */
  sheet_scale?: number;
  /**
   * The layout rules the sheet is laid out by (`LAYOUT_VERSION` in
   * drawing-sheet-layout.ts); absent means 1, a sheet saved before version 2.
   */
  layout_version?: number;
  projection: "first" | "third";
  dimension_units: DimensionUnits;
  dimension_scale: number;
  /** Hex color used for dimension lines and labels. */
  dimension_color: string;
  /** Weight multiplier for the sheet border and drawing outline. */
  drawing_outline_scale: number;
  outline_scale: number;
  /** Draw hidden linework solid instead of dashed; absent means dashed. */
  solid_hidden_lines?: boolean;
  additional_views: string[];
  border_columns: string;
  border_rows: string;
  axis_flips: AxisFlips;
  /** Hidden lines one body behind are told from deeper ones (the Level 2 depth mode). */
  hidden_depth: boolean;
  /** Views skipped from this drawing; the server keeps this list. */
  skipped_views?: string[];
  /** Full sections, each drawn as a view of its own (exact engine). */
  sections?: SectionView[];
};

export type OrthographicView = "front" | "back" | "top" | "bottom" | "right" | "left";

/**
 * A full section: the orthographic view `view` with everything nearer the eye
 * than the cutting plane taken away, labelled SECTION A-A by its letter, its
 * cutting-plane line drawn on `parent`.
 */
export type SectionView = {
  /** A to Z without I, O and Q. */
  id: string;
  view: OrthographicView;
  /** The plane's model coordinate along the view's line of sight (x, y or z), model units. */
  at: number;
  parent: string;
};

export type OutputSettings = { pdf: boolean; dwg: boolean };
export type ViewTransform = { x: number; y: number; scale: number; rotation?: number };
export type MeasurementSide = "top" | "right" | "bottom" | "left";
export type CustomDimension = {
  /** Placed from the STEP file's PMI; Auto dimension's detail never replaces it. */
  source_pmi?: {
    key: string;
    start: ViewerPoint;
    end: ViewerPoint;
    vertex?: ViewerPoint;
    peers?: ViewerPoint[];
    model_bounds: { min: ViewerPoint; max: ViewerPoint };
    label_offset?: Point;
    /** The file's tolerance in millimetres (degrees for angles); absent once edited. */
    tolerance?: DimensionTolerance;
    /** Each frame row's tolerance in millimetres; absent once the frame is edited. */
    frame?: number[];
    /** Further PMI items drawn on this entry (frames and datums under a size). */
    attached?: string[];
  };
  automatic?: {
    key: string;
    /** Changed by the user since Auto dimension placed it: Detail leaves it. */
    edited: boolean;
    start: ViewerPoint;
    end: ViewerPoint;
    vertex?: ViewerPoint;
    callout?: MeasuredFeature;
    peers?: { key: string; start: ViewerPoint }[];
    model_bounds: { min: ViewerPoint; max: ViewerPoint };
    /** Label offset from the projected anchor, in millimetres. */
    label_offset?: Point;
  };
  format?: DimensionFormat;
  /**
   * Annotation text. On an annotation `start` is the point it marks and
   * `label_point` where its content sits; nothing is measured.
   */
  note?: string;
  /** Additional anchors sharing a counted callout's single label. */
  additional_leaders?: Point[];
  annotation?: AnnotationContent;
  /** Angle dimensions: where the two measured edges meet. */
  vertex?: Point;
  id: string;
  view_id: string;
  /** Set when a 3D-authored measure has a valid 2D projection. */
  source_viewer_id?: string;
  start: Point;
  end: Point;
  mode?: MeasurementMode;
  feature?: MeasurementFeature;
  /** View-space point used as the label center; absent means automatic placement. */
  label_point?: Point;
  /**
   * Where the label was dropped in the 3D viewer, kept because its projection
   * onto the sheet cannot give the point back. Valid while `label_point` is
   * still `sheet_point`; moving the label on the sheet takes over.
   */
  viewer_label?: { point: ViewerPoint; sheet_point: Point };
  /**
   * Distance from the measured points to the dimension line, in view units.
   * Absent means the automatic placement, which steps outward until the line
   * clears the model outline.
   */
  offset?: number;
};
export type MeasurementPlacements = Record<MeasurementSide, boolean>;
export type ViewCrop = { minX: number; minY: number; maxX: number; maxY: number };
export type EditorPresentation = {
  hidden_views: Record<string, boolean>;
  hidden_centerlines: Record<string, boolean>;
  depth_modes: Record<string, DepthMode>;
  /** Fade hidden linework by how deeply it is buried. */
  depth_shading: boolean;
  view_labels: Record<string, string>;
  measurement_placements: Record<string, MeasurementPlacements>;
  custom_dimensions: CustomDimension[];
  view_crops: Record<string, ViewCrop>;
  viewer: ViewerSettings;
  /** The STEP file's semantic PMI: offered at upload, then imported or declined. */
  source_pmi?: SourcePmiChoice;
};
export type SourcePmiChoice = "offered" | "imported" | "declined";
export type EditorState = {
  views: Record<string, ViewTransform>;
  title_block: TitleBlock;
  settings: DrawingSettings;
  output: OutputSettings;
  presentation?: EditorPresentation;
};

/**
 * `hidden` is linework just under the surface - at most one body between it and
 * the eye - and is what the default view draws dashed. `hidden-deep` is buried
 * further; it is carried so X-ray can reveal it, but held back otherwise
 * because on a dense assembly it is most of the geometry.
 */
export type DrawingSpecLineStyle =
  | "visible"
  | "border"
  | "title"
  | "hidden"
  | "hidden-deep"
  | "center"
  | "dimension"
  /** Section lining across a section view's cut faces. */
  | "hatch"
  /** A section's cutting-plane line across the view it is cut from. */
  | "cutting";

/** What a section view's cutting plane cuts from one body (see Rust `CutRegion`). */
export type CutRegion = {
  /** The section view it lies on (`section-a`). */
  group: string;
  /** The body, in the model's placement order: neighbours are lined at different angles. */
  body: number;
  /** Closed rings, y up like the lines; the material is inside an odd number of them. */
  rings: [number, number][][];
  /** False where the file's faces do not close: outlined, not lined. */
  hatched: boolean;
};
/** The drawing spec format this build reads and writes (Rust `DRAWING_SPEC_VERSION`). */
export const DRAWING_SPEC_VERSION = 2;
/**
 * A circle or ellipse on the sheet, `p(t) = c + cos t·a + sin t·b` with `a`
 * and `b` conjugate semi-diameters (Rust `Conic`).
 */
export type SheetCurve = { c: [number, number]; a: [number, number]; b: [number, number] };

/** A NURBS curve that a run of lines samples from its first point to its last; exports write it as the curve it is. */
export type SheetSpline = {
  degree: number;
  knots: number[];
  points: [number, number][];
  /** One per point; absent when every weight is one. */
  weights?: number[];
};

export type DrawingSpec = {
  format_version: number;
  page: { width_pt: number; height_pt: number; name: string };
  lines: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    style: DrawingSpecLineStyle;
    custom_dimension_id?: string;
    /** The circle or ellipse this chord samples: an index into `curves`. */
    curve?: number;
    /** The spline this chord samples: an index into `splines`. */
    spline?: number;
  }[];
  /** Circles and ellipses that runs of lines sample; drawn as curves where their chords lie on them. */
  curves?: SheetCurve[];
  /** Splines that runs of lines sample, moved with their lines. */
  splines?: SheetSpline[];
  /** Section views' cut faces, placed as their lines are. */
  regions?: CutRegion[];
  /** The sections a geometry spec's section views were solved for. */
  sections?: SectionView[];
  texts: {
    value: string;
    x: number;
    y: number;
    size: number;
    custom_dimension_id?: string;
    /** Browser only: how a measurement's label follows its view when the view is turned. */
    attachment?: import("./canvas-geometry").TextAttachment;
    /** Browser only: a sheet mark's lettering (a section's cutting-plane letters), drawn plain and left where it is. */
    sheet_mark?: true;
  }[];
  groups: {
    id: string;
    points_per_mm?: number;
    line_ranges: [number, number][];
    text_ranges: [number, number][];
    /** Retained streaming footprint in model mm, before visible-outline refinement. */
    layout_size_mm?: [number, number];
    /** Browser-only outer lanes for overall dimensions; removed when exporting. */
    dimension_gaps?: Partial<Record<MeasurementSide, number>>;
  }[];
  title_block: TitleBlock;
  /** Rust supplies model units and measured extents; the editor draws labels. */
  model_units: string;
  view_dimensions: { id: string; horizontal: number; vertical: number }[];
  /** Bounds of the imported solids in the STEP file's axes, millimetres. */
  model_bounds?: { min: [number, number, number]; max: [number, number, number] } | null;
  dimension_scale?: number;
  dimension_color?: string;
  drawing_outline_scale?: number;
  outline_scale?: number;
  solid_hidden_lines?: boolean;
  /** Views the user skipped: where each would sit (y up, points). Nothing is drawn there. */
  skipped_views?: SkippedView[];
  /** A note every sheet of this drawing carries above the title block: the Lite engine's, so an exported Lite drawing says what it is. */
  sheet_note?: string;
  /** The company logo in the title block, as outlines on the sheet (points, y up). */
  logo?: { paths: import("./logo-artwork").LogoPath[] };
};

export type SkippedView = { id: string; x: number; y: number; width: number; height: number };

/** Compact geometry transport; expanded into DrawingSpec at the browser boundary. */
export type CompactDrawingSpec = Omit<DrawingSpec, "lines" | "curves"> & {
  encoding: "flat-v1";
  coordinates: number[];
  styles: string;
  /** Each curve as centre, then the two semi-diameters. */
  curves?: number[][];
  /** Runs of lines naming one curve: first line, count, curve. */
  curve_runs?: [number, number, number][];
  /** Runs of lines naming one spline: first line, count, spline. */
  spline_runs?: [number, number, number][];
};

export type Defaults = {
  standard: DrawingSettings["standard"];
  sheet_size: SheetSizeCode;
  sheet_style?: SheetStyleCode;
  sheet_auto?: boolean;
  sheet_orientation?: "landscape" | "portrait";
  quality: Quality;
  projection: DrawingSettings["projection"];
  dimension_units: DrawingSettings["dimension_units"];
  /** Units for Analysis and other readouts outside a drawing ("model": the file's own). */
  display_units?: DrawingSettings["dimension_units"];
  border_columns: string;
  border_rows: string;
  pdf: boolean;
  dwg: boolean;
  /** Calculate one body behind (Level 2) in the next conversion. */
  hidden_depth: boolean;
};

/**
 * The four drawing engines: Specbound's two, which read the model with
 * BrepKit, and OpenCascade's two. The values are the profile names they had
 * before v0.2.0, so an earlier release still reads the drawings.
 */
export type Quality = "exact-preview" | "lite" | "draft" | "fast-precise";

export type QualityOption = {
  value: Quality;
  title: string;
  detail: string;
  select_label: string;
  badge?: string;
};

export const QUALITY_OPTIONS: ReadonlyArray<QualityOption> = [
  {
    value: "exact-preview",
    title: "Exact B-rep",
    detail:
      "Specbound's own hidden-line engine: every line resolved on the true model geometry. Large assemblies take a few minutes.",
    select_label: "Exact B-rep (Specbound, recommended)",
    badge: "Recommended",
  },
  {
    value: "lite",
    title: "Lite",
    detail:
      "Specbound's quick preview: the same curves as Exact B-rep, with hidden lines found on a fine mesh of the model. Seconds where the exact engine takes minutes; not certified.",
    select_label: "Lite (Specbound, quick preview)",
    badge: "Preview",
  },
  {
    value: "draft",
    title: "OCCT HLR",
    detail:
      "OpenCascade's exact hidden-line removal, with true curved linework. Slower on large assemblies.",
    select_label: "OCCT HLR (OpenCascade)",
  },
  {
    value: "fast-precise",
    title: "OCCT polygonal",
    detail:
      "OpenCascade's hidden-line removal on a mesh of the model: much faster, with curves drawn as the mesh's short straight segments.",
    select_label: "OCCT polygonal (OpenCascade)",
  },
];

/** What to call a drawing's engine, a retired one included. */
export function qualityLabel(job: Pick<Job, "quality" | "retired_engine">) {
  if (job.retired_engine) return `${job.retired_engine} (retired)`;
  return QUALITY_OPTIONS.find((option) => option.value === job.quality)?.title ?? job.quality;
}

const THEME_VALUES = ["light", "dark"] as const;
const STANDARD_VALUES = ["ansi", "iso"] as const;
const PROJECTION_VALUES = ["first", "third"] as const;

function isStringValue(value: unknown, allowed: readonly string[]): value is string {
  return typeof value === "string" && allowed.includes(value);
}

export const isTheme = (value: unknown): value is Theme => isStringValue(value, THEME_VALUES);
export const isQuality = (value: unknown): value is Quality =>
  QUALITY_OPTIONS.some((option) => option.value === value);
export const isStandard = (value: unknown): value is Defaults["standard"] =>
  isStringValue(value, STANDARD_VALUES);
export const isProjection = (value: unknown): value is Defaults["projection"] =>
  isStringValue(value, PROJECTION_VALUES);

function isOptionalString(value: unknown) {
  return value === undefined || typeof value === "string";
}

function isOptionalBoolean(value: unknown) {
  return value === undefined || typeof value === "boolean";
}

export function isDefaultsPatch(value: unknown): value is Partial<Defaults> {
  if (typeof value !== "object" || value === null) return false;
  const patch = value as Record<string, unknown>;
  return (
    (patch.standard === undefined || isStandard(patch.standard)) &&
    (patch.sheet_size === undefined ||
      isSheetSize(patch.sheet_size, "ansi") ||
      isSheetSize(patch.sheet_size, "iso")) &&
    (patch.sheet_style === undefined || isSheetStyle(patch.sheet_style)) &&
    (patch.sheet_auto === undefined || typeof patch.sheet_auto === "boolean") &&
    (patch.sheet_orientation === undefined ||
      patch.sheet_orientation === "landscape" ||
      patch.sheet_orientation === "portrait") &&
    (patch.quality === undefined || isQuality(patch.quality)) &&
    (patch.projection === undefined || isProjection(patch.projection)) &&
    (patch.dimension_units === undefined || isDimensionUnitsValue(patch.dimension_units)) &&
    (patch.display_units === undefined || isDimensionUnitsValue(patch.display_units)) &&
    isOptionalString(patch.border_columns) &&
    isOptionalString(patch.border_rows) &&
    isOptionalBoolean(patch.pdf) &&
    isOptionalBoolean(patch.dwg) &&
    isOptionalBoolean(patch.hidden_depth)
  );
}

export type Point = { x: number; y: number };
export type SettingsTab = "general" | "current" | "drawing" | "output" | "canvas";
/** What opened the plan dialog: a catalog feature or limit key, and for a limit the server's message. */
export type UpgradePrompt = { feature: string; reason?: string };
export type IconName =
  | "grid"
  | "files"
  | "file"
  | "plus"
  | "folder"
  | "save"
  | "export"
  | "cube"
  | "analytics"
  | "refresh"
  | "settings"
  | "help"
  | "chevron"
  | "upload"
  | "delete"
  | "dots"
  | "undo"
  | "redo"
  | "select"
  | "hand"
  | "ruler"
  | "reset"
  | "eye"
  | "download"
  | "lock"
  | "annotate"
  | "parts";
